import React from 'react';

import filter from 'lodash/filter';
import {useSelector} from 'react-redux';

import {HeadingText} from 'app/components/general/HeadingText';
import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {fetchArticleFeedback} from 'app/kbs/actions';
import {denormalizeArticleFeedbackList} from 'app/kbs/datafetchers';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import {Feedback, ManagerResolveDiv} from './Feedback';
import {goToArticleDocEditURL} from './utils';

import {KBArticleSerializedType} from 'sweet-types';

interface Props {
  article: KBArticleSerializedType;
  show: boolean;
  onClose: () => void;
}

export function KBArticleFeedbackModal({
  article,
  show,
  onClose,
}: Props): JSX.Element {
  const [showResolvedFeedback, setShowResolvedFeedback] = React.useState(false);

  const {results: feedbackList, loading} = useSelector((state) =>
    denormalizeArticleFeedbackList(state, {
      article_id: article.id,
    })
  );

  const actions = useAsyncActions({fetchArticleFeedback});

  React.useEffect(() => {
    if (show) {
      actions.fetchArticleFeedback({article_id: article.id});
    }
  }, [article.id, show]);

  const {positive, total} = article.feedback;

  const {percent, negative, pendingFeedbackList, resolvedFeedbackList} =
    React.useMemo(() => {
      const negative = total - positive;
      const percent =
        total > 0
          ? {
              positive: `${Math.round((positive / total) * 100)}%`,
              negative: `${Math.round((negative / total) * 100)}%`,
            }
          : null;

      const pendingFeedbackList = filter(
        feedbackList,
        (f) => f.status === 'pending'
      );
      const resolvedFeedbackList = filter(
        feedbackList,
        (f) => f.status !== 'pending'
      );

      return {
        percent,
        negative,
        pendingFeedbackList,
        resolvedFeedbackList,
      };
    }, [positive, total, feedbackList]);

  const articleName = <b>{article.name}</b>;

  const resolvedFeedbackHeader =
    resolvedFeedbackList.length > 0 ? (
      <div className="d-flex justify-content-between align-items-center">
        <HeadingText size="0.8rem">
          {gt('Resolved Feedback')}
          <span className="text-secondary ml-1" style={{color: '#AAA'}}>
            ({resolvedFeedbackList.length})
          </span>
        </HeadingText>
        <button
          className="btn btn-link btn-sm"
          onClick={() => setShowResolvedFeedback((s) => !s)}
        >
          {showResolvedFeedback ? gt('Hide') : gt('Show')}
        </button>
      </div>
    ) : null;

  return (
    <TabbedModal
      show={show}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'main-tab',
          name: gt('Article Feedback'),
          icon: 'far fa-comment-alt-lines',
          content: (
            <div>
              <p className="lead">{articleName}</p>
              <div className="d-flex small">
                <div className="flex-grow-1 text-left">
                  <div className="font-weight-bold">
                    {ngt('%s opinion', '%s opinions', positive)}
                  </div>
                  <div>{gt('article answered my question')}</div>
                </div>
                <div className="flex-grow-1 text-right">
                  <div className="font-weight-bold">
                    {ngt('%s opinion', '%s opinions', negative)}
                  </div>
                  <div>{gt('article did not answer my question')}</div>
                </div>
              </div>

              {percent != null ? (
                <div className="progress mb-3">
                  <div
                    className="progress-bar bg-success"
                    style={{
                      width: percent.positive,
                    }}
                  >
                    {percent.positive}
                  </div>
                  <div
                    className="progress-bar bg-danger"
                    style={{
                      width: percent.negative,
                    }}
                  >
                    {percent.negative}
                  </div>
                </div>
              ) : null}

              {loading && feedbackList.length === 0 ? (
                <div className="border-top border-bottom font-italic text-muted py-4 text-center mb-3">
                  <i className="fa fa-circle-notch fa-spin mr-1" />
                  {gt('Loading')}
                </div>
              ) : (
                <>
                  <div className="mt-4 mb-1">
                    <HeadingText size="0.8rem">
                      {gt('Pending Feedback')}
                      <span
                        className="text-secondary ml-1"
                        style={{color: '#AAA'}}
                      >
                        ({pendingFeedbackList.length})
                      </span>
                    </HeadingText>
                  </div>
                  <div className="border-top mb-2">
                    {pendingFeedbackList.map((feedback) => (
                      <Feedback key={feedback.id} feedback={feedback} />
                    ))}
                  </div>

                  {resolvedFeedbackList.length > 0 ? (
                    <div className="mb-4 mt-4">
                      {showResolvedFeedback ? (
                        resolvedFeedbackHeader
                      ) : (
                        <ManagerResolveDiv>
                          {resolvedFeedbackHeader}
                        </ManagerResolveDiv>
                      )}
                      {showResolvedFeedback ? (
                        <div className="border-top mb-2">
                          {resolvedFeedbackList.map((feedback) => (
                            <Feedback key={feedback.id} feedback={feedback} />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}

              <StyledModalFooter>
                <button
                  className="btn btn-link text-secondary"
                  onClick={onClose}
                >
                  <i className="fa fa-times mr-1" />
                  {gt('Close')}
                </button>
                <button
                  className="btn btn-link"
                  onClick={() => goToArticleDocEditURL(article)}
                >
                  {gt('Edit article')}
                </button>
              </StyledModalFooter>
            </div>
          ),
        },
      ]}
    />
  );
}
